import { Component, OnInit } from '@angular/core';
import { BaseComponent, UserActionService } from 'src/app/shared';
import { UserRoleService } from '../../services/user-role.service';
import { UserActions } from 'src/app/shared/models/user';
import { TranslationRequest } from 'src/app/shared/models/translation/translation-request';
import { ManagerResult } from 'src/app/shared/models/search-result';
import { TranslationPipe } from '../../pipes/translation.pipe';
import { Session } from '../../services/session.service';
import { OmniApi } from '../../services/api/omni-api.service';
import { OmniDashboard } from '../../models/omni-dashboard';
import { SummaryDashboardFilterValue } from '../controls/filter-by-manager.component';

@Component({
    selector: 'app-summary-dashboard',
    template: `
    @if (!userrole.auditor) {
      <app-section>
        <div class="app-pl2 tw-w-full">
          <app-claim-breadcrumb
            [helpText]="summaryHelpText"
          ></app-claim-breadcrumb>
        </div>
        <app-notifications-header class="tw-w-full" [translationRequest]="translationRequest"></app-notifications-header>
        @if (isFilterByManagerShown) {
          <app-filter-by-manager
            class="tw-w-full"
            [translationRequest]="translationRequest"
            [initialSupervisor]="filterInitialSupervisor"
            [initialIncludeIndirect]="filterInitialIncludeIndirect"
            (supervisorSelected)="onSupervisorSelected($event)"
            (filterChange)="onFilterChange($event)"
          ></app-filter-by-manager>
        }
        <app-panel class="tw-w-full" [border]="'Empty'" [canExpand]="false">
          <div class="tw-w-full">
            <app-card-dashboard [omniDashboard]="omniDashboard" [translationRequest]="translationRequest" [error]="error"></app-card-dashboard>
          </div>
        </app-panel>
      </app-section>
    }
    `,
    styles: [``],
    standalone: false
})
export class SummaryDashboardComponent extends BaseComponent implements OnInit {
  summaryHelpText: string = '';
  omniDashboard: OmniDashboard = null;
  includeIndirectsInSession: string;
  filteredManagerInSession: any;
  error:any;
  filterInitialSupervisor: ManagerResult | null = null;
  filterInitialIncludeIndirect: boolean = false;
  currentFilter: SummaryDashboardFilterValue = {
    supervisor: null,
    includeIndirect: false,
  };
  isFilterByManagerShown: boolean = false;

  translationRequest = new TranslationRequest({
    language: sessionStorage.getItem('languagepreference'),
    key: null,
    contNum: 0,
    tableName: null,
    primaryKey: null,
    primaryValue: null,
    lob: null,
    group: 'Shared',
    subGroup: 'Shared',
  });

  constructor(
    public userrole: UserRoleService,
    private userAction: UserActionService,
    private translationPipe: TranslationPipe,
    private session: Session,
    private omniApi: OmniApi,
  ) {
    super();
    this.userAction.setAction(UserActions.ManagerSummaryView);
    if (userrole.omni) {
      this.summaryHelpText = 'The Summary View includes all employees.';
    }
    if (userrole.overseer) {
      this.summaryHelpText =
        'The Summary View includes all employees in which you have supervisory, oversight or administrator responsibilities';
    }
    if (userrole.supervisor) {
      this.summaryHelpText = 'The Summary View includes supervised employees.';
    }
  }

  async removeFromSessionStorage(itemToRemove: any) {
    sessionStorage.removeItem(itemToRemove);
  }

  async ngOnInit() {
    this.userAction.setAction(UserActions.ManagerSummaryView);
    !this.session.user ? await this.session.loadUser() : null;
    this.isFilterByManagerShown = !this.session.user.isFirstLevelSupervisor;
    this.translationPipe.transform('Filter by', this.translationRequest);
    this.translationPipe.transform('Manager', this.translationRequest);
    this.checkSessionStorage();
    this.currentFilter = {
      supervisor: this.filterInitialSupervisor,
      includeIndirect: this.filterInitialIncludeIndirect,
    };
    await this.getOmniDashboard();
  }

  onFilterChange(value: SummaryDashboardFilterValue): void {
    this.currentFilter = value;
    sessionStorage.setItem(
      this.session.user.sessionId + 'includeIndirects_summary',
      value.includeIndirect.toString()
    );
    this.getOmniDashboard();
  }

  async onSupervisorSelected(supervisor: ManagerResult | null) {
    this.FeatureLogging(
      'FilterByManager',
      supervisor ? '' : ' - Removed manager from summary view',
      'Filter By Manager in summary view'
    );
    if (supervisor && supervisor.empUnum != null) {
      sessionStorage.setItem(
        this.session.user.sessionId + 'filteredManager_summary',
        JSON.stringify(supervisor)
      );
      this.userAction.setAction(UserActions.FilterByManager);
    } else if (supervisor === null) {
      this.removeFromSessionStorage(
        this.session.user.sessionId + 'filteredManager_summary'
      );
      this.userAction.setAction(UserActions.ManagerSummaryView);
      this.filterInitialSupervisor = null;
    }
    await this.getOmniDashboard();
  }

  async getOmniDashboard() {
    this.omniDashboard = null;
    let managerEmpUnum = '';
    let includeIndirectReports = false;
    if (this.checkSessionStorage()) {
      managerEmpUnum = this.filterInitialSupervisor.empUnum;   
      this.userAction.setAction(UserActions.FilterByManager);   
    }

    includeIndirectReports = this.currentFilter.includeIndirect;

    try {
      if (managerEmpUnum && this.userAction.getAction() == UserActions.FilterByManager) {
        this.omniDashboard = await this.omniApi.getOmniDashboard(
          managerEmpUnum,
          includeIndirectReports
        );
      } else {
        this.omniDashboard = await this.omniApi.getOmniDashboard(
          '',
          includeIndirectReports
        );
      }
    } catch (error) {
      /* empty */
      this.error = error;
    }
  }

  checkSessionStorage(): boolean {
    let filteredManagerInSession = false;
    this.includeIndirectsInSession = sessionStorage.getItem(
      this.session.user.sessionId + 'includeIndirects_summary'
    );
    this.filteredManagerInSession = sessionStorage.getItem(
      this.session.user.sessionId + 'filteredManager_summary'
    );
    
    if (this.includeIndirectsInSession) {
      this.filterInitialIncludeIndirect =
        this.includeIndirectsInSession === 'true';
    } else {
      this.filterInitialIncludeIndirect = false;
    }
    if (this.filteredManagerInSession) {
      this.filterInitialSupervisor = JSON.parse(
        this.filteredManagerInSession
      );
      filteredManagerInSession = true;
    }
    return filteredManagerInSession;
  }
}
